import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import i18n from '../../i18n/config';
import { clearApiAuthLost, markApiAuthLost } from '../auth/apiAuthSession';
import { getUserToken, clearUserToken } from '../auth/userToken';
import { isNetworkError } from '../config/constants';
import { formatApiDetail, humanizeDetailText } from '../notifications/sources';
import { useNotificationStore } from '../store/notificationStore';

// Каноничный axios instance для UI.
// Orval использует этот файл как mutator (см. orval.config.cjs).
// Пустой baseURL: запросы к /api/* идут на тот же origin; в production nginx проксирует /api на бэкенд.
const API_BASE_URL =
  typeof __VITE_API_URL__ !== 'undefined' && String(__VITE_API_URL__ ?? '').trim()
    ? String(__VITE_API_URL__).replace(/\/+$/, '')
    : '';
// Не используем Authorization: Bearer — Caddy perimeter занимает Authorization: Basic.
const LOCAL_UI_TOKEN_HEADER = 'X-AlgoSys-Local-Token';

// Таймаут по умолчанию: без него при недоступности бэкенда запросы висят и UI тормозит
const DEFAULT_TIMEOUT_MS = 15000;
const HEALTH_POLL_CONFIG = { skipErrorNotification: true } as const;

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  timeout: DEFAULT_TIMEOUT_MS,
});

export { HEALTH_POLL_CONFIG };

export default api;

export const AXIOS_INSTANCE = api;

// D11: на каждый запрос — только per-user JWT (после Google-входа). Статического
// build-time токена в бандле нет: он давал бы всем посетителям идентичность local-ui с ролью owner.
// Без токена запрос уходит без заголовка и получает честный 401 (см. markApiAuthLost ниже).
api.interceptors.request.use((config) => {
  const token = getUserToken();
  if (token) {
    // Axios 1.x: заголовки — AxiosHeaders; корректно ставим через .set(), иначе fallback на объект.
    const h = config.headers as unknown as { set?: (k: string, v: string) => void } | undefined;
    if (h && typeof h.set === 'function') h.set(LOCAL_UI_TOKEN_HEADER, token);
    else
      config.headers = {
        ...((config.headers as Record<string, string>) ?? {}),
        [LOCAL_UI_TOKEN_HEADER]: token,
      } as never;
  }
  return config;
});

const toMessageList = (error: unknown): string[] => {
  const asRecord = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  const out: string[] = [];
  const err = asRecord(error);
  const resp = asRecord(err?.response);
  const data = asRecord(resp?.data);
  const detail = data?.detail;

  if (typeof detail === 'string' && detail.trim()) out.push(detail.trim());
  if (Array.isArray(detail)) {
    for (const item of detail) {
      const line = formatApiDetail(item);
      if (line) out.push(line);
    }
  } else if (detail != null) {
    const line = formatApiDetail(detail);
    if (line) out.push(line);
  }
  if (out.length === 0) {
    const msg = typeof err?.message === 'string' ? err.message.trim() : '';
    if (msg) out.push(msg);
  }
  return out.slice(0, 8);
};

let notificationInterceptorAttached = false;
/** Не трактовать 401 как потерю сессии сразу после gateway/network сбоя (API перезапуск). */
let lastGatewayOrNetworkErrorAt = 0;
const AUTH_LOST_GRACE_MS = 120_000;

if (!notificationInterceptorAttached) {
  notificationInterceptorAttached = true;
  AXIOS_INSTANCE.interceptors.response.use(
    (response) => {
      if (response.config?.url?.includes('/api/health/healthz')) {
        clearApiAuthLost();
      }
      return response;
    },
    (error) => {
      const asRecord = (v: unknown): Record<string, unknown> | null =>
        v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
      const err = asRecord(error);
      const code = String(err?.code || '');
      if (code === 'ERR_CANCELED') return Promise.reject(error);

      const cfg = asRecord(err?.config);
      if (cfg?.skipErrorNotification === true) return Promise.reject(error);

      const resp = asRecord(err?.response);
      const status = typeof resp?.status === 'number' ? resp.status : 0;
      if (status === 429) return Promise.reject(error);
      const isGatewayError = status === 502 || status === 503 || status === 504;
      if (isGatewayError || isNetworkError(error) || status === 0) {
        lastGatewayOrNetworkErrorAt = Date.now();
      }
      if (
        status === 401 &&
        Date.now() - lastGatewayOrNetworkErrorAt > AUTH_LOST_GRACE_MS
      ) {
        // D11 self-heal: протухший/невалидный per-user JWT → чистим его и перезагружаемся,
        // чтобы снова показать «Войти через Google», а не блокировать UI.
        if (getUserToken()) {
          clearUserToken();
          if (typeof window !== 'undefined') window.location.reload();
        } else {
          markApiAuthLost();
        }
      }
      const method = String(cfg?.method || 'GET').toUpperCase();
      const url = String(cfg?.url || '/api');
      const severity = status >= 500 ? 'critical' : 'warning';
      const isNet = isGatewayError || isNetworkError(error) || status === 0;
      const title =
        status > 0
          ? i18n.t('notifications:apiError.titleHttp', { status, method })
          : i18n.t('notifications:apiError.titleNetwork', { method });
      const messages = toMessageList(error);
      const list = messages.length ? messages : [i18n.t('notifications:apiError.messageFallback')];
      try {
        const push = useNotificationStore.getState().push;
        if (isGatewayError) {
          push({
            severity,
            source: 'health',
            title: i18n.t('notifications:apiError.gatewayTitle', { status }),
            message: i18n.t('notifications:apiError.gatewayMessage'),
            ts: Date.now(),
            dedupKey: `api:gateway:${status}`,
            link: '/notifications',
          });
        } else if (list.length === 1) {
          push({
            severity,
            source: 'user-action',
            title,
            message: humanizeDetailText(list[0]!, i18n.t('notifications:apiError.messageFallback')),
            ts: Date.now(),
            dedupKey: isNet ? `api:network:${method}` : `api:${status}:${method}:${url}:${list[0]}`,
            link: '/notifications',
          });
        } else {
          const batchTitle =
            status > 0
              ? i18n.t('notifications:apiError.titleBatch', { status, method, count: list.length })
              : i18n.t('notifications:apiError.titleBatchNetwork', { method, count: list.length });
          const joined = list
            .slice(0, 5)
            .map((line) => humanizeDetailText(line, line))
            .join('; ');
          push({
            severity,
            source: 'user-action',
            title: batchTitle,
            message: joined + (list.length > 5 ? '…' : ''),
            ts: Date.now(),
            dedupKey: `api:${status}:${method}:${url}:batch`,
            link: '/notifications',
          });
        }
      } catch (e) {
        if (typeof console !== 'undefined' && console.error) {
          console.error('[Notifications] Failed to push API error to store', e);
        }
      }
      return Promise.reject(error);
    },
  );
}

type CancelablePromise<T> = Promise<T> & { cancel?: () => void };

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): CancelablePromise<T> => {
  const source = axios.CancelToken.source();

  const promise = AXIOS_INSTANCE({
    ...config,
    ...(options ?? {}),
    cancelToken: source.token,
  }).then((r: AxiosResponse<T>) => r.data) as CancelablePromise<T>;

  promise.cancel = () => source.cancel('Request cancelled');
  return promise;
};

